import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { startsWithExplain } from "utils/helpers/Query/startsWithExplain";
import { lazyWithPreload } from "utils/lazyWithPreload";
import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { AiSurfaceState, useAiAccessPolicy } from "hooks/useAiAccessPolicy";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";

import CopilotIcon from "assets/icons/Copilot.svg?react";

import ErrorMessage from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/ErrorMessage/ErrorMessage";
import ResultsControls from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/ResultsControls/ResultsControls";
import { RunningQuery } from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/RunningQuery/RunningQuery";
import TabsBar from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/TabsBar/TabsBar";
import OutputSubPanel from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/components/OutputSubPanel/OutputSubPanel";
import {
  isQueryStatementCompleted,
  queryStatementHasErrorToDisplay,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/getQueryStatementState";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { useScripts } from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { getDocumentScript } from "pages/DevelopWorkspace/helpers/getDocumentScript";
import { isDocumentRunning } from "pages/DevelopWorkspace/helpers/isDocumentRunning";
import {
  AiStateExternalInitializer,
  DocumentOutputTab,
  ExecutionType,
  QueryStatement,
  QueryStatementStatus,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import Button, { ButtonSize, ButtonTemplate } from "components/Button";
import LoadingOverlap from "components/LoadingOverlap";
import Tooltip from "components/Tooltip";

import { TextExplainResult } from "./TextExplainResult";

import styles from "./DocumentResults.module.scss";

interface DocumentResultsProps {
  document: WorkspaceDocument;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

const ExplainStatement = lazyWithPreload(
  () => import("../ExplainStatement/ExplainStatement")
);

const Datagrid = lazyWithPreload(() => import("./Table/Datagrid"));

const isJSON = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

export const EMPTY_RESULT_PLACEHOLDER = {
  data: [{ "": "The query has been executed successfully.", No: 1 }],
  meta: [
    {
      name: "No",
      type: "",
      displayName: "No",
    },
    {
      name: "",
      type: "string",
      displayName: "",
    },
  ],
  rows: 1,
};

export const EMPTY_RESULT_PLACEHOLDER_WITH_NETWORK_ERROR = {
  data: [
    {
      "": "Result is not available due to a network interruption.",
      Status: "Success",
    },
  ],
  meta: [
    {
      name: "Status",
      type: "",
      displayName: "Status",
    },
    {
      name: "",
      type: "string",
      displayName: "",
    },
  ],
  rows: 1,
};

const DocumentResults = (props: DocumentResultsProps) => {
  const { document, expanded, setExpanded } = props;
  const { t } = useTranslation();
  const aiPolicy = useAiAccessPolicy();
  const {
    actions: {
      switchDocumentActiveQueryStatement,
      fixQueryStatement,
      cancelAIOperation,
    },
    qsResults,
  } = useDocuments();

  const { state: scriptsState } = useScripts();

  const { data: engines } = useWorkspaceEngines({
    includeSystemEngine: true,
  });

  usePreloadComponents([ExplainStatement, Datagrid]);

  if (!document) {
    return null;
  }

  if (!document.execution) {
    return null;
  }

  const documentIsRunning = isDocumentRunning(document);

  const activeQueryStatementIndex =
    document.execution.activeQueryStatementIndex;

  const activeQueryStatement =
    document.execution.queryStatements[activeQueryStatementIndex];

  const script = getDocumentScript(document, scriptsState);

  const visibleQueryStatements = document.execution.queryStatements.filter(
    qs => {
      const qsResult = qsResults[qs.id];

      return isQueryStatementCompleted(qs) && (qsResult?.data || qs.error);
    }
  );

  const renderQueryResults = (
    queryStatement: QueryStatement,
    isActive: boolean
  ) => {
    const qsResult = qsResults[queryStatement.id];

    if (!(qsResult?.data || queryStatement.error)) {
      return null;
    }

    if (queryStatementHasErrorToDisplay(queryStatement)) {
      const message =
        typeof queryStatement.error === "string"
          ? queryStatement.error
          : t("document_results.an_error_occurred");

      const getAIButtonProps = () => {
        const isQueryStatementOutdated = script
          ? script.editorState.doc.slice(
              activeQueryStatement.location.start,
              activeQueryStatement.location.end
            ) !== activeQueryStatement.content
          : true;

        if (!document.aiState || document.aiState.text.trim() === "") {
          if (isQueryStatementOutdated) {
            return {
              text: t("document_results.fix_with_copilot"),
              disabled: true,
              startIcon: <CopilotIcon />,
              tooltip: {
                title: t("document_results.out_of_sync"),
              },
              onClick: () => {
                fixQueryStatement(document.id, queryStatement.id, message);
              },
            };
          }

          return {
            text: t("document_results.fix_with_copilot"),
            disabled: false,
            startIcon: <CopilotIcon />,
            onClick: () => {
              fixQueryStatement(document.id, queryStatement.id, message);
            },
          };
        }

        if (
          document.aiState.externalInitializer ===
            AiStateExternalInitializer.FixQueryStatement &&
          document.aiState?.isLoading
        ) {
          return {
            text: t("document_results.stop"),
            disabled: false,
            startIcon: <CopilotIcon />,
            onClick: () => {
              cancelAIOperation(document.id);
            },
          };
        }

        return {
          text: t("document_results.fix_with_copilot"),
          disabled: true,
          startIcon: <CopilotIcon />,
          tooltip: {
            title: t("document_results.finish_ai_operation"),
          },
          onClick: () => {},
        };
      };

      if (aiPolicy.editor !== AiSurfaceState.Enabled) {
        return <ErrorMessage errorMessage={message} />;
      }

      const aiButtonProps = getAIButtonProps();

      return (
        <ErrorMessage
          errorMessage={message}
          actionsBar={
            <Tooltip
              title={aiButtonProps.tooltip?.title}
              placement="top-end"
            >
              <div>
                <Button
                  onClick={aiButtonProps.onClick}
                  template={ButtonTemplate.Secondary}
                  startIcon={aiButtonProps.startIcon}
                  size={ButtonSize.Small}
                  text={aiButtonProps.text}
                  disabled={aiButtonProps.disabled}
                />
              </div>
            </Tooltip>
          }
        />
      );
    }

    if (!qsResult?.data) {
      return null;
    }

    const result = qsResult.data;
    const meta = qsResult.meta;

    if (
      startsWithExplain(queryStatement.content) &&
      meta?.length &&
      result.length
    ) {
      const key = meta[0].name;
      const explain = result[0][key];
      if (!isJSON(explain)) {
        return <TextExplainResult explain={explain} />;
      }
    }
    if (
      isActive &&
      document.execution?.executionType === ExecutionType.Explain
    ) {
      return (
        <ExplainStatement
          document={document}
          queryStatement={queryStatement}
        />
      );
    }
    if (meta?.length === 0) {
      return (
        <Datagrid
          data={EMPTY_RESULT_PLACEHOLDER.data}
          fields={EMPTY_RESULT_PLACEHOLDER.meta}
          isActive={isActive}
          queryStatement={queryStatement}
          isEmptyMeta
        />
      );
    }

    if (Array.isArray(result) && Array.isArray(meta)) {
      return (
        <Datagrid
          data={result}
          fields={meta}
          isActive={isActive}
          queryStatement={queryStatement}
        />
      );
    }
  };

  const renderBody = () => {
    return visibleQueryStatements.map((queryStatement, index) => {
      const isActive = index === activeQueryStatementIndex;
      return (
        <div
          key={queryStatement.id}
          className={classNames(styles.resultsContainer, {
            [styles.active]: isActive,
          })}
        >
          {renderQueryResults(queryStatement, isActive)}
        </div>
      );
    });
  };

  const handleSetActiveTab = (index: number) => {
    switchDocumentActiveQueryStatement({
      documentId: document.id,
      queryStatementIndex: index,
      userSelectedActiveQueryStatementIndexTimestamp: Date.now(),
      activeOutputTab: DocumentOutputTab.Results,
    });
  };

  const currentEngine = engines?.find(
    engine => engine.engineName === document.context.engine.name
  );

  return (
    <div className={classNames(styles.root, { [styles.expanded]: expanded })}>
      {activeQueryStatement.status === QueryStatementStatus.restoring && (
        <LoadingOverlap isLoading={true} />
      )}
      {documentIsRunning &&
        !visibleQueryStatements.length &&
        (currentEngine ? (
          <RunningQuery
            queryStatement={activeQueryStatement}
            engine={currentEngine}
          />
        ) : null)}

      {!!visibleQueryStatements.length && (
        <OutputSubPanel
          body={
            <TabsBar
              queryStatements={visibleQueryStatements}
              activeQueryStatementIndex={activeQueryStatementIndex}
              setActiveTab={handleSetActiveTab}
            />
          }
          controls={
            <>
              {activeQueryStatement && (
                <ResultsControls
                  expanded={expanded}
                  setExpanded={setExpanded}
                  queryStatement={activeQueryStatement}
                  document={document}
                />
              )}
            </>
          }
        />
      )}
      <div className={styles.body}>{renderBody()}</div>
    </div>
  );
};

export default DocumentResults;
